/**
 * Badge (design-system.md §10). One shape, a fixed set of tones. Severity tones are
 * critical/high/medium/low/info; status tones are open/in-progress/resolved/disputed;
 * generic tones (neutral/success/warning/danger/muted/accent) cover everything else.
 *
 * Deviation from the §10 table, recorded: the spec pairs `-500` foreground on `-100`
 * background, which fails WCAG AA for warning/success/info (≈2:1). Light mode uses the
 * `-700` foreground instead; dark mode uses `-300` on `-900` (§2.5). Borders stay `-500`.
 */
import type { HTMLAttributes, ReactNode } from 'react';

export type BadgeTone =
  | 'critical'
  | 'high'
  | 'medium'
  | 'low'
  | 'info'
  | 'open'
  | 'in-progress'
  | 'resolved'
  | 'disputed'
  | 'neutral'
  | 'success'
  | 'warning'
  | 'danger'
  | 'muted'
  | 'accent';

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  tone?: BadgeTone | undefined;
  icon?: ReactNode | undefined;
}

export function Badge({
  tone = 'neutral',
  icon,
  className,
  children,
  ...rest
}: BadgeProps): JSX.Element {
  return (
    <span
      className={['argus-badge', `argus-badge--${tone}`, className ?? ''].filter(Boolean).join(' ')}
      {...rest}
    >
      {icon}
      {children}
    </span>
  );
}
